/**
Project: Voice Panel (c)
Title: Region 
Description: Component to select the regions.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Flex, Text, Checkbox, Button } from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import PageUtil from "../../../../core/helpers/page-util";
import ResponseUtil from "../../../../core/helpers/response-util";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import AlertSuccessMessage from "../../common/helpers/alert-success-message";
import IntlUtil from "../../../../core/helpers/intl-util";
import PageLoader from "../../common/helpers/page-loader";
import { manageError } from "../../../../core/actions/common-actions";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  setDomainSetupProgressPage,
  getDomainRegions,
  saveDomainRegions,
} from "../actions/domain-setup-action";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import { cloneDeep } from "lodash";
import PageExitGuard from "../../common/helpers/page-exit-guard";

class Region extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_domainsetup = "vpa_domainsetup";

  constructor(props) {
    super(props);
    this.state = {
      isPageDataFetched: false,
      isFormDataFilled: false,
      configStatus: false,
      domainRegions: [],
      errorMessage: null,
      successMessage: null,
      errorDetails: null,
      isError: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.checkAccountNumber();
    await this.loadPageData();
  }
  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  handleGetRegionsData = async () => {
    let domainsList = [];
    await this.props.domainRegions.forEach(async (region) => {
      let regions = { ...region, updateStatus: region.configStatus };
      domainsList.push(regions);
    });
    let status = domainsList.some((rgn) => {
      if (rgn.updateStatus === 0) {
        return true;
      } else {
        return false;
      }
    });

    await this.setStateAsync({
      domainRegions: domainsList,
      isFormDataFilled: status,
    });
  };

  checkAccountNumber = async () => {
    if (
      this.props.domainSetupProgressAccountNumber === null ||
      this.props.domainSetupProgressAccountNumber.trim() === ""
    ) {
      await this.props.setDomainSetupProgressPage(
        VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.WELCOME
      );
    }
  };

  loadPageData = async () => {
    try {
      await this.setStateAsync({ isPageDataFetched: false });
      await this.props.getDomainRegions(
        this._cancelToken
      );
      await this.handleGetRegionsData();
      await this.setStateAsync({ isPageDataFetched: true });
    } catch (err) {
      await this.setState({ isError: true });
      await manageError(err, this.props.history);
    }
  };

  handleCheckboxOptionChange = async (event, option) => {
    await this.setState((prevState) => {
      const result = [...prevState.domainRegions];
      result.forEach((region) => {
        if (region.regionName === option.label) {
          if (option.checked === true) {
            region.updateStatus = 1;
          } else {
            region.updateStatus = -1;
          }
        }
        return { domainRegions: cloneDeep(result) };
      });
    });
    let selectedRegions = this.state.domainRegions.filter(
      (rgn) => rgn.configStatus !== 1
    );
    let isDataSelected = selectedRegions.some((rgn) => {
      if (rgn.updateStatus === 1 || rgn.updateStatus === 0) {
        return true;
      } else {
        return false;
      }
    });
    await this.setStateAsync({
      isFormDataFilled: isDataSelected,
    });
  };

  loadNextPage = async () => {
    await this.props.setDomainSetupProgressPage(
      VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.DOMAINS
    );
  };

  loadHomePage = async () => {
    await this.props.setDomainSetupProgressPage(
      VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.WELCOME
    );
    this.props.history.replace(VoiceAdminURLProps.home);
  };

  handleRegionsSubmit = async () => {
    if (
      this.state.domainRegions &&
      this.state.domainRegions.length > 0
    ) {
      this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_domainsetup,
          "notification.info.pleaseWaitUpdatingData"
        )
      );
      const domainRegions = {
        regions: this.state.domainRegions,
      };
      await saveDomainRegions(domainRegions, this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            this.setStateAsync({
              successMessage: res.data.message,
            });
            this.props.hidePageWaitDialog();
            this.loadNextPage();
          } else {
            this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "notification.error.regionsCreateFailed"
              ),
            });
            this.props.hidePageWaitDialog();
          }
        })
        .catch(async (err) => {
          this.props.hidePageWaitDialog();
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            const primeError = ResponseUtil.getPrimeError(err);
            if (
              primeError &&
              primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.REGIONS_CREATE_FAILED
            ) {
              await this.setStateAsync({
                errorDetails: primeError.message,
              });
            }
            this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "notification.error.regionsCreateFailed"
              ),
            });
          } else {
            await this.setState({ isError: true });
            await manageError(err, this.props.history);
          }
        });
    }
  };

  render() {
    return (
      <Grid fluid className="page-grid m-t-15">
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-heading4">
              {IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "content.SelectRegions.regionTitle"
              )}
            </Text>
          </Col>
        </Row>
        <PageLoader
          status={this.state.isPageDataFetched}
          size="small"
          labelPosition="end"
          label={IntlUtil.getText(
            this._intl_ns_common,
            "content.loadingInprogress"
          )}
          type="inline"
        >
          {this.state.errorMessage !== null ? (
            <Row>
              <Col xs={12} className="m-t-20 m-b-20">
                <AlertErrorMessage
                  content={this.state.errorMessage}
                  moreDetails={{
                    label: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.moreDetails"
                    ),
                    content: this.state.errorDetails,
                  }}
                />
              </Col>
            </Row>
          ) : this.state.successMessage !== null ? (
            <Row>
              <Col xs={12} className="m-t-20 m-b-20">
                <AlertSuccessMessage content={this.state.successMessage} />
              </Col>
            </Row>
          ) : null}
          {this.props.domainRegions && this.props.domainRegions.length > 0 ? (
            <Row>
              <Col xs={12} sm={12} md={12} lg={6}>
                <Flex column>
                  <Row>
                    <Col xs={12} className="m-b-10">
                      <Text className="page-text">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_domainsetup,
                          "content.SelectRegions.regiontext"
                        )}
                      </Text>
                    </Col>
                  </Row>
                  {this.state.domainRegions.map((region, index) => {
                    return (
                      <Row key={`region-${index}`}>
                        <Col xs={12} className="m-b-10">
                          <Checkbox
                            disabled={region.configStatus === 1}
                            label={region.regionName}
                            defaultChecked={
                              region.configStatus === 1 ||
                              region.configStatus === 0
                            }
                            onChange={this.handleCheckboxOptionChange}
                            className="page-checkbox"
                          />
                        </Col>
                      </Row>
                    );
                  })}
                </Flex>
              </Col>
            </Row>
          ) : (
            <>
              <Row>
                <Col xs={12} className="m-b-15 m-t-15">
                  <AlertErrorMessage
                    content={
                      <>
                        {IntlUtil.getText(
                          this._intl_ns_vpa_domainsetup,
                          "notification.error.retrieveRegionsFailed"
                        )}
                      </>
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="m-t-15 m-b-15">
                  <Button
                    primary
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.home"
                    )}
                    onClick={this.loadHomePage}
                    className="vpa-voice-setup-button"
                  />
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col xs={12} className="m-t-15 m-b-15">
              <Button
                primary
                content={IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "content.continue"
                )}
                disabled={!this.state.isFormDataFilled}
                onClick={this.handleRegionsSubmit}
                className="vpa-voice-setup-button"
              />
            </Col>
          </Row>
        </PageLoader>
        <PageExitGuard
          when={this.state.isError === false}
          navigate={(path) => this.props.history.push(path)}
          shouldBlockNavigation={(location) => {
            if (location.pathname) {
              return true;
            }

            return false;
          }}
        />
      </Grid>
    );
  }
}
const mapStateToProps = (state) => ({
  domainSetupProgressPage: state.domainSetupStore.domainSetupProgressPage,
  domainSetupProgressAccountNumber:
    state.domainSetupStore.domainSetupProgressAccountNumber,
  domainRegions: state.domainSetupStore.domainRegions,
});

const mapActionToProps = {
  showPageWaitDialog,
  hidePageWaitDialog,
  setDomainSetupProgressPage,
  getDomainRegions,
};

export default connect(mapStateToProps, mapActionToProps)(Region);
